import { KeyboardEvent, ReactNode, useId, useState } from 'react';

import { FOCO_VISIVEL } from 'utils/foco';

interface InfoTooltipProps {
  /** Nome acessível do botão (ex.: "Como é calculado: Galeria", "Regras dos Destaques da Cultura"). */
  rotulo: string;
  /** Texto do tooltip. */
  children: ReactNode;
  /** Largura do tooltip. Padrão: até 290px, como no Extrato de Premiação. */
  largura?: string;
  /** Lado em que o balão abre. Padrão: acima do ícone. Um texto longo no alto da tela abre abaixo, para não ser cortado. */
  abrePara?: 'cima' | 'baixo';
}

/**
 * Ícone de informação com o tooltip da nova identidade (`.jj-tipwrap` do design do Extrato de Premiação):
 * caixa `brand-blue-dark` com texto branco. Abre com hover e com foco, fecha com Esc (WCAG 1.4.13) e fica
 * ligado ao botão por `aria-describedby`.
 *
 * Abre pelo hover ou foco do ancestral `group/tip` e se ancora no início do ancestral `relative` (os dois
 * podem ser o mesmo elemento). Fechado, sai do layout (`hidden`), para não criar rolagem horizontal em telas
 * estreitas.
 */
export default function InfoTooltip({
  rotulo,
  children,
  largura = 'w-[min(290px,calc(100vw-120px))]',
  abrePara = 'cima',
}: InfoTooltipProps) {
  const idDoTexto = useId();
  const [fechadoPorEsc, setFechadoPorEsc] = useState(false);

  function aoTeclar(evento: KeyboardEvent) {
    if (evento.key === 'Escape') setFechadoPorEsc(true);
  }

  return (
    <>
      <button
        type="button"
        aria-label={rotulo}
        aria-describedby={idDoTexto}
        onKeyDown={aoTeclar}
        onBlur={() => setFechadoPorEsc(false)}
        onMouseLeave={() => setFechadoPorEsc(false)}
        className={`flex cursor-pointer rounded-full border-0 bg-transparent p-0 text-brand-blue ${FOCO_VISIVEL}`}
      >
        <svg
          width="15"
          height="15"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
          focusable="false"
        >
          <circle cx="12" cy="12" r="9" />
          <path d="M12 16v-4" />
          <path d="M12 8h.01" />
        </svg>
      </button>
      <span
        id={idDoTexto}
        role="tooltip"
        className={`absolute ${abrePara === 'cima' ? 'bottom-[calc(100%+8px)]' : 'top-[calc(100%+8px)]'} left-0 z-[5] hidden ${largura} rounded-lg bg-brand-blue-dark px-[13px] py-[11px] text-left font-text text-[11px] font-normal leading-[1.55] text-brand-white shadow-[0_10px_24px_rgba(0,45,75,0.24)] ${
          fechadoPorEsc ? '' : 'group-focus-within/tip:block group-hover/tip:block'
        }`}
      >
        {children}
      </span>
    </>
  );
}
